import React from 'react';
import { fillFor } from '../wall/wallTokens';
import { T, RADIUS, viewLimitLabel } from './messagesModel';
import ViewLimitIcon from './ViewLimitIcon';
import { MAX_ALBUM_ITEMS, trayMeta, tileDuration, needsTrim } from './albumModel';

/**
 * The composer's multi-select tray (handoff §1): what has been picked, before it goes.
 *
 * A grey card above the field: `{n} SELECTED` with the photo / video / size meta, the
 * view-limit pill (one limit for the whole album) and REMOVE ALL; a horizontal strip of 76px
 * tiles — the first wears COVER, every one has ×, a clip shows its length and a TRIM chip when
 * it will be cut — and a trailing add tile while there is room. Tapping a tile makes it the
 * cover. The hint under it says so.
 */
export default function AlbumTray({
  items,
  viewLimit = 0,
  viewOptionsOpen = false,
  onToggleViewOptions,
  onMakeCover,
  onRemove,
  onRemoveAll,
  onTrim,
  onAddMore,
}) {
  const n = items.length;
  const viewOnce = viewLimit > 0;
  const room = MAX_ALBUM_ITEMS - n;
  return (
    <div data-testid="album-tray" className="mb-2 font-mono" style={{ background: T.off, borderRadius: RADIUS.row, padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
        <span className="font-bold" style={{ fontSize: 11, letterSpacing: '.1em' }}>{n} SELECTED</span>
        <span style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey500 }}>{trayMeta(items)}</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="composer-photo-view-limit"
          aria-haspopup="menu"
          aria-expanded={viewOptionsOpen}
          onClick={onToggleViewOptions}
          className="font-bold flex items-center"
          style={{ height: 28, borderRadius: RADIUS.pill, border: '2px solid transparent', padding: '0 12px 0 6px', gap: 6, fontSize: 10, letterSpacing: '.1em', background: viewOnce ? fillFor('on') : fillFor('open'), color: viewOnce ? T.white : T.ink, cursor: 'pointer' }}
        >
          <ViewLimitIcon n={viewLimit || 1} check={!viewOnce} size={18} style={{ color: viewOnce ? '#fff' : '#000' }} />
          {viewLimitLabel(viewLimit)}
        </button>
        <button
          type="button"
          onClick={onRemoveAll}
          className="font-bold"
          style={{ height: 28, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.white, padding: '0 12px', fontSize: 10, letterSpacing: '.1em', cursor: 'pointer' }}
        >
          {n > 1 ? 'REMOVE ALL' : 'REMOVE'}
        </button>
      </div>

      <div className="flex overflow-x-auto pr-faces-strip" style={{ gap: 8, paddingBottom: 2 }}>
        {items.map((p, i) => {
          const cover = i === 0 && n > 1;
          const dur = tileDuration(p);
          return (
            <div
              key={p.clientId}
              data-testid="album-tray-tile"
              data-cover={cover ? 'true' : undefined}
              className="relative flex-shrink-0"
              style={{ width: 76, height: 76, borderRadius: RADIUS.small, overflow: 'hidden', background: '#000', outline: cover ? `2px solid ${T.ink}` : 0, outlineOffset: -2 }}
            >
              <button
                type="button"
                onClick={() => onMakeCover?.(p.clientId)}
                aria-label={cover ? 'The cover' : 'Make this the cover'}
                style={{ position: 'absolute', inset: 0, padding: 0, border: 0, background: 'none', cursor: n > 1 ? 'pointer' : 'default' }}
              >
                {p.kind === 'video'
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  ? <video src={p.url} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <img src={p.url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              </button>
              {cover && (
                <span className="font-bold pointer-events-none" style={{ position: 'absolute', left: 5, top: 5, height: 16, padding: '0 5px', borderRadius: RADIUS.pill, background: T.ink, color: T.white, fontSize: 7, letterSpacing: '.12em', display: 'flex', alignItems: 'center' }}>COVER</span>
              )}
              <button
                type="button"
                onClick={() => onRemove?.(p.clientId)}
                aria-label={`Remove ${p.name || 'item'}`}
                style={{ position: 'absolute', right: 4, top: 4, width: 20, height: 20, borderRadius: '50%', border: 0, padding: 0, background: 'rgba(255,255,255,.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="square"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              {p.kind === 'video' && dur && (
                <span className="font-bold pointer-events-none flex items-center" style={{ position: 'absolute', left: 5, bottom: 4, gap: 3, fontSize: 8, letterSpacing: '.08em', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,.7)' }}>
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="#fff"><path d="M6 3l15 9-15 9z" /></svg>{dur}
                </span>
              )}
              {needsTrim(p) && (
                <button
                  type="button"
                  data-testid="composer-retrim"
                  onClick={() => onTrim?.(p.clientId)}
                  className="font-bold"
                  style={{ position: 'absolute', right: 4, bottom: 18, height: 16, padding: '0 5px', borderRadius: RADIUS.pill, border: 0, background: T.white, color: T.ink, fontSize: 7, letterSpacing: '.1em', cursor: 'pointer' }}
                >
                  TRIM
                </button>
              )}
            </div>
          );
        })}
        {room > 0 && (
          <button
            type="button"
            data-testid="album-tray-add"
            onClick={onAddMore}
            aria-label="Add more"
            className="flex-shrink-0 flex flex-col items-center justify-center font-bold"
            style={{ width: 76, height: 76, borderRadius: RADIUS.small, border: `2px solid ${T.ink}`, background: T.white, gap: 4, boxSizing: 'border-box', cursor: 'pointer' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square"><path d="M5 12h14M12 5v14" /></svg>
            <span style={{ fontSize: 8, letterSpacing: '.1em' }}>{room} MORE</span>
          </button>
        )}
      </div>

      <span style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey500 }}>
        {n > 1 ? 'TAP ONE TO MAKE IT THE COVER · SENDS AS ONE ALBUM' : 'ADD MORE TO SEND AN ALBUM'}
      </span>
    </div>
  );
}
